import React, { createContext, useContext } from 'react'
import datasetsCreatorReducer from '../reducers/DatasetsCreatorReducer'

// State interface
export interface DatasetsCreatorState {
  originalCsvData: any
  csvData: any
  formData: {
    title: string
    description: string
    hasHeader: boolean
    dataType: string
  }
  selectedColumns: string[]
  keyColumn: string | null
  currentStep: 'upload' | 'create' | 'preview' | undefined
  isLoading: boolean
  error: string | null
}

// Initial state definition
const initialState = {
  originalCsvData: null,
  csvData: null,
  formData: {
    title: '',
    description: '',
    hasHeader: true,
    dataType: 'row',
  },
  selectedColumns: [],
  keyColumn: null,
  currentStep: undefined, // 'upload' | 'create' | 'preview' | undefined
  isLoading: false,
  error: null,
}

interface DatasetsCreatorContext extends DatasetsCreatorState {
  setOriginalCSV?: (data: any) => void
  uploadCSV?: (data: any) => void
  updateFormData?: (data: any) => void
  setSelectedColumns?: (columns: string[]) => void
  setKeyColumn?: (column: string) => void
  setCurrentStep?: (step: string) => void
  setLoading?: (isLoading: boolean) => void
  setError?: (error: string | null) => void
  resetState?: () => void
  transposeData?: (data: string[][], startIndex?: number) => string[][]
}

// Create the context
export const DatasetsCreatorContext = createContext<DatasetsCreatorContext>(initialState)

// Provider component
export const DatasetsCreatorProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = React.useReducer(datasetsCreatorReducer, initialState)

  // Helper methods for actions
  const setOriginalCSV = React.useCallback((data: any) => {
    dispatch({ type: 'set_original_csv_data', payload: data })
  }, [])

  const uploadCSV = React.useCallback((data: any) => {
    dispatch({ type: 'set_csv_data', payload: data })
  }, [])

  const updateFormData = React.useCallback((data: any) => {
    dispatch({ type: 'update_form_data', payload: data })
  }, [])

  const setSelectedColumns = React.useCallback((columns: string[]) => {
    dispatch({ type: 'set_selected_columns', payload: columns })
  }, [])

  const setKeyColumn = React.useCallback((column: string) => {
    dispatch({ type: 'set_key_column', payload: column })
  }, [])

  const setCurrentStep = React.useCallback((step: string) => {
    dispatch({ type: 'set_current_step', payload: step })
  }, [])

  const setLoading = React.useCallback((isLoading: boolean) => {
    dispatch({ type: 'set_loading', payload: isLoading })
  }, [])

  const setError = React.useCallback((error: string | null) => {
    dispatch({ type: 'set_error', payload: error })
  }, [])

  const resetState = React.useCallback(() => {
    dispatch({ type: 'reset_state' })
  }, [])

  const transposeData = React.useCallback((data: string[][], startIndex: number = 0): string[][] => {
    if (!data?.length) return []
    
    const rows = data.length - startIndex
    const cols = data[0].length
    const result: string[][] = Array.from({ length: cols }, () => new Array(rows))
    
    for (let rowIndex = 0; rowIndex < rows; rowIndex++)
      for (let colIndex = 0; colIndex < cols; colIndex++)
        result[colIndex][rowIndex] = data[rowIndex + startIndex][colIndex]
    
    return result;
  }, [])

  React.useEffect(() => {
    if (state.csvData || state.originalCsvData || state.currentStep || state.error) {
    }
  }, [state])

  const value = {
    ...state,
    setOriginalCSV,
    uploadCSV,
    updateFormData,
    setSelectedColumns,
    setKeyColumn,
    setCurrentStep,
    setLoading,
    setError,
    resetState,
    transposeData,
    dispatchDatasetsCreator: dispatch,
  }

  return <DatasetsCreatorContext.Provider value={value}>{children}</DatasetsCreatorContext.Provider>
}

// Custom hook for using the context
export const useDatasetsCreator = () => {
  const context = useContext(DatasetsCreatorContext)
  if (context === undefined) {
    throw new Error('useDatasetsCreator must be used within a DatasetsCreatorProvider')
  }
  return context
}
